import { useCallback, useEffect, useState } from 'react';
import { api, errorMessage } from '../../lib/api.ts';
import { useToast, type ToastApi } from '../../lib/useToast.ts';
import type { BankPayload } from '../../../shared/api.ts';
import type { BankCategory, BankItem, BankStatus } from '../../../shared/deck.ts';

/**
 * The bank's data + mutation plumbing, shared by both mounts (the Library
 * page's manage surface and the Assets drawer's picker).
 *
 * `run` is the studio's useBusyActions shape with one deliberate difference:
 * a failure raises a TOAST, not a window.alert. The bank is the one surface
 * that has no native dialogs left, and useBusyActions' alert is shared with
 * the Dashboard and Library, so it stays as it is.
 */
type BankState = {
  status: 'loading' | 'ready' | 'error';
  items: BankItem[];
  categories: BankCategory[];
  error: string | null;
};

export function useBank() {
  const [state, setState] = useState<BankState>({
    status: 'loading',
    items: [],
    categories: [],
    error: null,
  });
  const [busy, setBusy] = useState(false);
  const { toast, pushToast, runUndo, dismissToast } = useToast();

  const reload = useCallback(async () => {
    try {
      const data = await api.get<Partial<BankPayload>>('/api/bank');
      setState({ status: 'ready', items: data.items || [], categories: data.categories || [], error: null });
    } catch (err) {
      setState({ status: 'error', items: [], categories: [], error: errorMessage(err) });
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  /** Every mutation goes through here: one busy flag, and a failure surfaces
   *  as an error toast. Never throws. */
  const run = useCallback(
    async <T>(fn: () => Promise<T>, failPrefix: string): Promise<T | undefined> => {
      setBusy(true);
      try {
        return await fn();
      } catch (err) {
        pushToast(`${failPrefix}: ${errorMessage(err)}`, { tone: 'error' });
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [pushToast],
  );

  return { state, reload, busy, run, toast, pushToast, runUndo, dismissToast };
}

/** Archive or restore an item, with the inverse wired to the toast's Undo.
 *  Shared because both the card grid and the facts editor offer it. */
export function makeSetItemStatus({
  run,
  reload,
  pushToast,
}: {
  run: <T>(fn: () => Promise<T>, failPrefix: string) => Promise<T | undefined>;
  reload: () => Promise<void>;
  pushToast: ToastApi['pushToast'];
}) {
  return async function setItemStatus(item: BankItem, status: BankStatus) {
    await run(async () => {
      await api.put(`/api/bank/items/${item.id}`, { status });
      await reload();
      pushToast(status === 'archived' ? `Archived "${item.label}"` : `Restored "${item.label}"`, {
        undo: async () => {
          await api.put(`/api/bank/items/${item.id}`, { status: status === 'archived' ? 'active' : 'archived' });
          await reload();
        },
      });
    }, 'Update failed');
  };
}
